import { html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { CompanySelectorWebComponent } from '../../../companySelector';
import { GzQueryInputWebComponent } from '../../../ui/gzQueryInput';
import { GzDatetimeFilterWebComponent } from '../../../ui/gzDatetimeFilter';
import { ActionBtnProps, DateFilterProps, SearchQueryPanelEvents, SearchQueryProps } from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { DateTimeFilterTranslateStrings } from '../../../ui/gzDatetimeFilter/types';
import { WEB_COMPONENT_GZ_TOOLTIP } from '../../../webcomponents.constants';
import { AvailableCompaniesSource } from '../../../../../../libs/shared/dtos/types/dashboards/src/lib/shared-dtos-dashboards-gz-console';

export class XdrSearchQueryPanelWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  pageTitle = '';

  @property({ type: Object, attribute: true, reflect: true })
  searchQueryProps: SearchQueryProps = {
    queryInputPlaceholder: '',
    queryBtnString: '',
    clearBtnString: '',
    errorMessages: {},
    hintTextString: '',
    helperHref: '',
  };

  @property({ type: Object, attribute: true, reflect: true })
  dateFilterProps: DateFilterProps = {
    label: '',
    labelWidth: '',
    placeholder: '',
    timezone: '',
    timeRangeOptions: [
      { label: 'Last 24 hours', value: 1 },
      { label: 'Last 7 days', value: 7 },
      { label: 'Last 30 days', value: 30 },
    ],
    userLocale: '',
    min: (new Date().getTime() - 1000 * 60 * 60 * 24 * 7) / 1000,
    max: new Date().getTime() / 1000,
  };

  @property({ type: Object, attribute: true, reflect: true })
  actionBtnProps: ActionBtnProps = {
    saveBtnString: 'Save',
    saveAsBtnString: 'Save as',
    discardBtnString: 'Discard changes',
  };
  @query('gz-company-selector')
  companySelector: CompanySelectorWebComponent;
  @query('gz-search-query')
  searchQuery: GzQueryInputWebComponent;
  @query('gz-datetime-filter')
  datetimeFilter: GzDatetimeFilterWebComponent;

  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="header">
          <div class="smartviews-header">
            <gz-title class="title" fontweight="500" fontsize="20">${this.pageTitle}</gz-title>
            <div class="smartviews-actions">
              <gz-tooltip class="tooltip" tooltipText="${translateFn('Not available in demo mode.')}">
                <gz-button
                  class="advancedSearchSave"
                  type="quiet"
                  color="secondary"
                  buttonheight="small"
                  buttonfontsize="small"
                  lowercase
                  @click="${this.handleSaveClick}"
                  >${translateFn('Save#preserve state')}
                </gz-button>
              </gz-tooltip>
              <gz-tooltip class="tooltip" tooltipText="${translateFn('Not available in demo mode.')}">
                <gz-button
                  class="advancedSearchSave"
                  type="quiet"
                  color="secondary"
                  buttonheight="small"
                  buttonfontsize="small"
                  lowercase
                  @click="${this.handleSaveAsClick}"
                  >${this.actionBtnProps.saveAsBtnString}
                </gz-button>
              </gz-tooltip>
              <!--
              // TODO: implement discard changes
              <gz-button
                type="quiet"
                color="secondary"
                buttonheight="small"
                buttonfontsize="small"
                lowercase
                @click="${this.handleDiscardClick}"
                >${this.actionBtnProps.discardBtnString}
              </gz-button>
-->
            </div>
          </div>

          <div class="smartviews-filters">
            <gz-datetime-filter
              showTimePickers
              menuPosition="right"
              label=${this.dateFilterProps.label}
              labelWidth=${this.dateFilterProps.labelWidth}
              .min=${this.dateFilterProps.min}
              .max=${this.dateFilterProps.max}
              .userLocale=${this.dateFilterProps.userLocale}
              .timezone=${this.dateFilterProps.timezone}
              .timeRangeOptions=${this.dateFilterProps.timeRangeOptions}
              .translateStrings=${{
                confirmButton: translateFn('Apply'),
                cancelButton: translateFn('Cancel'),
                fromLabel: translateFn('From'),
                toLabel: translateFn('To'),
                customLabel: translateFn('Custom'),
                allToDate: translateFn('All (to date)'),
              } as DateTimeFilterTranslateStrings}
              class="pr-2"
            >
            </gz-datetime-filter>

            <gz-company-selector
              id="companySelectorDropdown"
              class="company-selector"
              data-company-selector-params="${JSON.stringify({
                options: {
                  source: AvailableCompaniesSource.FROM_EDR,
                  countRecords: false,
                },
              })}"
            ></gz-company-selector>
          </div>
        </div>

        <gz-search-query
          .runQueryBtnString=${this.searchQueryProps.queryBtnString}
          .clearBtnString=${this.searchQueryProps.clearBtnString}
          .queryInputPlaceholder=${this.searchQueryProps.queryInputPlaceholder}
          .errorMessages=${this.searchQueryProps.errorMessages}
          .hintTextString=${this.searchQueryProps.hintTextString}
          helperHref=${this.searchQueryProps.helperHref}
          .enterKeyEnabled=${this.searchQueryProps.enterKeyEnabled}
          class="px-2 py-1"
        >
        </gz-search-query>
      </div>
    `;
  }

  handleSaveClick() {
    this.dispatchEvent(
      new CustomEvent(SearchQueryPanelEvents.SAVE, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  handleSaveAsClick() {
    this.dispatchEvent(
      new CustomEvent(SearchQueryPanelEvents.SAVE_AS, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  handleDiscardClick() {
    this.dispatchEvent(
      new CustomEvent(SearchQueryPanelEvents.DISCARD, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  public setSelectedTarget(filters): void {
    this.companySelector.propertiesSet = false;
    this.companySelector.selectedTarget = {
      companyId: filters.companyId,
      companyName: filters.companyName,
    };
    this.companySelector.propertiesSet = true;
  }

  public disableSmartViewButtonsForDemoCompany(disable: boolean) {
    const buttons = this.shadowRoot?.querySelectorAll('.advancedSearchSave');
    const tooltips = this.shadowRoot?.querySelectorAll(WEB_COMPONENT_GZ_TOOLTIP);

    if (disable) {
      buttons?.forEach(button => {
        button?.setAttribute('disabled', 'true');
      });
    } else {
      buttons?.forEach(button => {
        button?.removeAttribute('disabled');
      });
      tooltips?.forEach(tooltip => {
        tooltip?.removeAttribute('tooltipText');
      });
    }
  }
}
